import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  Calculator, TrendingDown, TrendingUp, Server, Database, 
  Cloud, HardDrive, Cpu, MemoryStick, RefreshCw, Download
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart, Area, Line } from 'recharts';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const WhatIfCalculator = () => {
  const { shouldShowData } = useDemoData();

  const [scenario, setScenario] = useState({
    currentInstances: 50,
    instanceType: 'm5.large',
    utilizationRate: 35,
    reservedPercentage: 20,
    spotPercentage: 0,
    storageGB: 5000,
    dataTransferGB: 2000,
  });

  const [results, setResults] = useState(null);

  // Pricing data (simplified)
  const pricing = {
    'm5.large': { onDemand: 0.096, reserved: 0.060, spot: 0.038 },
    'm5.xlarge': { onDemand: 0.192, reserved: 0.120, spot: 0.076 },
    'm5.2xlarge': { onDemand: 0.384, reserved: 0.240, spot: 0.152 },
    'c5.large': { onDemand: 0.085, reserved: 0.053, spot: 0.034 },
    'c5.xlarge': { onDemand: 0.170, reserved: 0.106, spot: 0.068 },
    'r5.large': { onDemand: 0.126, reserved: 0.079, spot: 0.050 },
    'r5.xlarge': { onDemand: 0.252, reserved: 0.158, spot: 0.100 },
  };

  const calculateCosts = () => {
    const hoursPerMonth = 730;
    const instancePrice = pricing[scenario.instanceType];
    
    // Current cost (all on-demand)
    const currentMonthlyCost = scenario.currentInstances * instancePrice.onDemand * hoursPerMonth;
    
    // Optimized cost calculation
    const reservedInstances = Math.floor(scenario.currentInstances * (scenario.reservedPercentage / 100));
    const spotInstances = Math.floor(scenario.currentInstances * (scenario.spotPercentage / 100));
    const onDemandInstances = scenario.currentInstances - reservedInstances - spotInstances;
    
    const reservedCost = reservedInstances * instancePrice.reserved * hoursPerMonth;
    const spotCost = spotInstances * instancePrice.spot * hoursPerMonth;
    const onDemandCost = onDemandInstances * instancePrice.onDemand * hoursPerMonth;
    
    const optimizedMonthlyCost = reservedCost + spotCost + onDemandCost;
    
    // Right-sizing savings (based on utilization)
    const rightSizingFactor = scenario.utilizationRate < 40 ? 0.5 : scenario.utilizationRate < 60 ? 0.75 : 1;
    const rightSizedCost = optimizedMonthlyCost * rightSizingFactor;
    
    // Storage costs
    const storageCost = scenario.storageGB * 0.023; // $0.023 per GB
    const dataTransferCost = scenario.dataTransferGB * 0.09; // $0.09 per GB
    
    const totalCurrentCost = currentMonthlyCost + storageCost + dataTransferCost;
    const totalOptimizedCost = rightSizedCost + (storageCost * 0.7) + dataTransferCost; // 30% storage optimization
    
    const monthlySavings = totalCurrentCost - totalOptimizedCost;
    const annualSavings = monthlySavings * 12;
    const savingsPercentage = ((monthlySavings / totalCurrentCost) * 100).toFixed(1);

    setResults({
      current: {
        compute: currentMonthlyCost,
        storage: storageCost,
        transfer: dataTransferCost,
        total: totalCurrentCost
      },
      optimized: {
        compute: rightSizedCost,
        storage: storageCost * 0.7,
        transfer: dataTransferCost,
        total: totalOptimizedCost
      },
      savings: {
        monthly: monthlySavings,
        annual: annualSavings,
        percentage: savingsPercentage
      },
      breakdown: {
        reserved: reservedInstances,
        spot: spotInstances,
        onDemand: onDemandInstances,
        reservedSavings: (scenario.currentInstances * instancePrice.onDemand - reservedInstances * instancePrice.reserved) * hoursPerMonth,
        spotSavings: (scenario.currentInstances * instancePrice.onDemand - spotInstances * instancePrice.spot) * hoursPerMonth * (scenario.spotPercentage / 100),
        rightSizingSavings: optimizedMonthlyCost - rightSizedCost
      },
      projectedMonthly: [
        { month: 'Current', current: totalCurrentCost, optimized: totalCurrentCost },
        { month: 'Month 1', current: totalCurrentCost, optimized: totalCurrentCost * 0.95 },
        { month: 'Month 2', current: totalCurrentCost, optimized: totalCurrentCost * 0.85 },
        { month: 'Month 3', current: totalCurrentCost, optimized: totalOptimizedCost },
        { month: 'Month 4', current: totalCurrentCost, optimized: totalOptimizedCost },
        { month: 'Month 5', current: totalCurrentCost, optimized: totalOptimizedCost },
        { month: 'Month 6', current: totalCurrentCost, optimized: totalOptimizedCost },
      ]
    });

    toast.success('Cost simulation completed!');
  };

  const comparisonData = results ? [
    { category: 'Compute', current: results.current.compute, optimized: results.optimized.compute },
    { category: 'Storage', current: results.current.storage, optimized: results.optimized.storage },
    { category: 'Transfer', current: results.current.transfer, optimized: results.optimized.transfer },
  ] : [];

  if (!shouldShowData) {
    return <EmptyDataState title="What-If Calculator" description="Connect your cloud account to see real data here." />;
  }


  return (
    <div className="space-y-6" data-testid="whatif-calculator-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
          <Calculator className="w-7 h-7 text-emerald-500" />
          What-If Cost Calculator
        </h1>
        <p className="text-slate-400 dark:text-slate-400 mt-1">
          Simulate different scenarios to optimize your cloud costs
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Panel */}
        <Card className="lg:col-span-1 border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg">Scenario Parameters</CardTitle>
            <CardDescription>Adjust values to simulate costs</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Instance Configuration */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                <Server className="w-4 h-4 text-emerald-500" />
                Compute
              </h3>
              
              <div>
                <Label>Number of Instances</Label>
                <Input
                  type="number"
                  value={scenario.currentInstances}
                  onChange={(e) => setScenario({ ...scenario, currentInstances: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div>
                <Label>Instance Type</Label>
                <Select 
                  value={scenario.instanceType} 
                  onValueChange={(v) => setScenario({ ...scenario, instanceType: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="m5.large">m5.large (General)</SelectItem>
                    <SelectItem value="m5.xlarge">m5.xlarge (General)</SelectItem>
                    <SelectItem value="m5.2xlarge">m5.2xlarge (General)</SelectItem>
                    <SelectItem value="c5.large">c5.large (Compute)</SelectItem>
                    <SelectItem value="c5.xlarge">c5.xlarge (Compute)</SelectItem>
                    <SelectItem value="r5.large">r5.large (Memory)</SelectItem>
                    <SelectItem value="r5.xlarge">r5.xlarge (Memory)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <Label>Avg. Utilization Rate</Label>
                  <span className="text-sm text-emerald-600 font-medium">{scenario.utilizationRate}%</span>
                </div>
                <Slider
                  value={[scenario.utilizationRate]}
                  onValueChange={([v]) => setScenario({ ...scenario, utilizationRate: v })}
                  max={100}
                  step={5}
                />
                <p className="text-xs text-slate-400 mt-1">
                  {scenario.utilizationRate < 40 ? 'Low - consider right-sizing' : 
                   scenario.utilizationRate < 70 ? 'Moderate - some optimization possible' : 'Good utilization'}
                </p>
              </div>
            </div>

            {/* Pricing Model */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                <Cpu className="w-4 h-4 text-emerald-500" />
                Pricing Model Mix
              </h3>

              <div>
                <div className="flex justify-between mb-2">
                  <Label>Reserved Instances</Label>
                  <span className="text-sm text-emerald-600 font-medium">{scenario.reservedPercentage}%</span>
                </div>
                <Slider
                  value={[scenario.reservedPercentage]}
                  onValueChange={([v]) => setScenario({ 
                    ...scenario, 
                    reservedPercentage: v,
                    spotPercentage: Math.min(scenario.spotPercentage, 100 - v)
                  })}
                  max={100 - scenario.spotPercentage}
                  step={5}
                />
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <Label>Spot Instances</Label>
                  <span className="text-sm text-amber-600 font-medium">{scenario.spotPercentage}%</span>
                </div>
                <Slider
                  value={[scenario.spotPercentage]}
                  onValueChange={([v]) => setScenario({ 
                    ...scenario, 
                    spotPercentage: v,
                    reservedPercentage: Math.min(scenario.reservedPercentage, 100 - v)
                  })}
                  max={100 - scenario.reservedPercentage}
                  step={5}
                />
              </div>

              <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                <p className="text-xs text-slate-400 dark:text-slate-400">
                  On-Demand: <span className="font-semibold">{100 - scenario.reservedPercentage - scenario.spotPercentage}%</span>
                </p>
              </div>
            </div>

            {/* Storage */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-emerald-500" />
                Storage & Transfer
              </h3>

              <div>
                <Label>Storage (GB)</Label>
                <Input
                  type="number"
                  value={scenario.storageGB}
                  onChange={(e) => setScenario({ ...scenario, storageGB: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div>
                <Label>Data Transfer (GB/month)</Label>
                <Input
                  type="number"
                  value={scenario.dataTransferGB}
                  onChange={(e) => setScenario({ ...scenario, dataTransferGB: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <Button 
              onClick={calculateCosts} 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              size="lg"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Calculate Savings
            </Button>
          </CardContent>
        </Card>

        {/* Results Panel */}
        <div className="lg:col-span-2 space-y-6">
          {results ? (
            <>
              {/* Savings Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-slate-200 dark:border-slate-800 bg-red-50 bg-gradient-to-br from-red-50 to-white dark:from-red-900/10 dark:to-slate-900">
                  <CardContent className="pt-6">
                    <p className="text-sm text-red-600 dark:text-red-400">Current Monthly Cost</p>
                    <p className="text-2xl font-bold text-red-700 dark:text-red-300">
                      ${results.current.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 dark:border-slate-800 bg-emerald-50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-900">
                  <CardContent className="pt-6">
                    <p className="text-sm text-emerald-600 dark:text-emerald-400">Optimized Cost</p>
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                      ${results.optimized.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 dark:border-slate-800 bg-blue-50 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-slate-900">
                  <CardContent className="pt-6">
                    <p className="text-sm text-emerald-600 dark:text-blue-400">Monthly Savings</p>
                    <p className="text-2xl font-bold text-emerald-700 dark:text-blue-300">
                      ${results.savings.monthly.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </p>
                    <Badge className="mt-2 bg-emerald-100 text-blue-800">
                      {results.savings.percentage}% reduction
                    </Badge>
                  </CardContent>
                </Card>
              </div>

              {/* Annual Savings Highlight */}
              <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-500 bg-gradient-to-r from-emerald-500 to-emerald-600">
                <CardContent className="py-6">
                  <div className="flex items-center justify-between text-white">
                    <div>
                      <p className="text-emerald-100">Projected Annual Savings</p>
                      <p className="text-4xl font-bold">${results.savings.annual.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                    </div>
                    <TrendingDown className="w-16 h-16 text-emerald-300" />
                  </div>
                </CardContent>
              </Card>

              {/* Charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-slate-200 dark:border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg">Cost Comparison</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={comparisonData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="category" />
                        <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} />
                        <Legend />
                        <Bar dataKey="current" fill="#ef4444" name="Current" />
                        <Bar dataKey="optimized" fill="#10b981" name="Optimized" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 dark:border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg">Projected Savings Timeline</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <ComposedChart data={results.projectedMonthly}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} />
                        <Legend />
                        <Area type="monotone" dataKey="current" fill="#fee2e2" stroke="#ef4444" name="Current" />
                        <Line type="monotone" dataKey="optimized" stroke="#10b981" strokeWidth={3} name="Optimized" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Optimization Breakdown */}
              <Card className="border-slate-200 dark:border-slate-800">
                <CardHeader>
                  <CardTitle className="text-lg">Optimization Strategy</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-emerald-50 dark:bg-blue-900/20 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Database className="w-5 h-5 text-emerald-600" />
                        <span className="font-semibold text-emerald-700 dark:text-blue-300">Reserved Instances</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-800 dark:text-blue-200">{results.breakdown.reserved}</p>
                      <p className="text-sm text-emerald-600">instances ({scenario.reservedPercentage}%)</p>
                    </div>

                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Cloud className="w-5 h-5 text-amber-600" />
                        <span className="font-semibold text-amber-700 dark:text-amber-300">Spot Instances</span>
                      </div>
                      <p className="text-2xl font-bold text-amber-800 dark:text-amber-200">{results.breakdown.spot}</p>
                      <p className="text-sm text-amber-600">instances ({scenario.spotPercentage}%)</p>
                    </div>

                    <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Server className="w-5 h-5 text-slate-400" />
                        <span className="font-semibold text-slate-700 dark:text-slate-300">On-Demand</span>
                      </div>
                      <p className="text-2xl font-bold text-slate-800 dark:text-slate-200">{results.breakdown.onDemand}</p>
                      <p className="text-sm text-slate-400">instances ({100 - scenario.reservedPercentage - scenario.spotPercentage}%)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border-slate-200 dark:border-slate-800 h-full flex items-center justify-center">
              <CardContent className="text-center py-16">
                <Calculator className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-400 dark:text-slate-400">Configure Your Scenario</h3>
                <p className="text-slate-400 mt-2">Adjust the parameters on the left and click "Calculate Savings" to see results</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatIfCalculator;
